/* eslint-disable no-undef */
import React, { useEffect, useState, useRef } from 'react';
import Carousel from '../../../components/Carousel/Carousel';
import Filter from '@components/Filter';
import { Styles } from './styles';
import PropertyCard from '@components/PropertyCard/PropertyCard';
import OperationFilter from '@components/OperationFilter/OperationFilter';
import AppBanner from '@components/Banner/appBanner';
import Header from '@components/Header';
import Footer from '@components/Footer/Footer';
import Navbar from '@components/Navbar';
import { usePropertyStore } from '@stores/Property/Property';
import { IProperty, IPropertySearchParameters } from '@stores/Property/types';
import PageControl from '@components/PageControl';
import { getPropertySearchParams } from '@utils/jquery';
import { useLocation } from 'react-router-dom'; // NEW
import SEO from '@components/SEO';

interface HomeScreenProps {
  defaultFilter?: 'VENDA' | 'ALUGUEL';
  showCarousel?: boolean;
  showOperationFilter?: boolean;
  filterDirection?: 'column' | 'row';
}

const HomeScreen: React.FC<HomeScreenProps> = ({
  defaultFilter,
  showCarousel = true,
  showOperationFilter = true,
  filterDirection = 'column'
}) => {
  const propertyListRef = useRef<HTMLDivElement>(null);
  const [properties, setProperties] = useState<IProperty[]>([]);
  const { searchProperties } = usePropertyStore();
  const location = useLocation(); // NEW

  const [selectedOperation, setSelectedOperation] = useState<
    'VENDA' | 'ALUGUEL' | 'TODOS'
  >(defaultFilter || 'TODOS'); // INIT FROM PROP

  // derive filters from URL + defaultFilter
  const queryParams = new URLSearchParams(location.search);
  const filters: IPropertySearchParameters = getPropertySearchParams(
    queryParams,
    defaultFilter
  );

  const [currentPage, setCurrentPage] = useState<number>(filters.page || 1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const handleOperationChange = (operation: 'VENDA' | 'ALUGUEL' | 'TODOS') => {
    setSelectedOperation(operation);
    const query: IPropertySearchParameters = { ...filters, page: 1 };
    query.operacao =
      operation !== 'TODOS' ? operation.toLowerCase() : undefined;

    searchProperties(query).then((response) => {
      setProperties(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotalPages(response.last_page);
    });
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    const query: IPropertySearchParameters = { ...filters, page };
    if (selectedOperation !== 'TODOS') {
      query.operacao = selectedOperation.toLowerCase();
    }
    searchProperties(query).then((response) => {
      setProperties(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotalPages(response.last_page);
      console.log(propertyListRef.current);
      if (propertyListRef.current) {
        window.scrollTo({
          top: propertyListRef.current.offsetTop - 100,
          left: 0,
          behavior: 'smooth'
        });
      }
    });
  };

  useEffect(() => {
    const fetchProperties = async () => {
      const query: IPropertySearchParameters = {
        ...filters,
        page: currentPage
      };
      if (selectedOperation !== 'TODOS') {
        query.operacao = selectedOperation.toLowerCase();
      }
      const response = await searchProperties(query);
      if (!response?.data) {
        setProperties([]);
        setCurrentPage(1);
        setLastPage(1);
        setTotalPages(1);
        return;
      }
      setProperties(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotalPages(response.last_page);
    };
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, defaultFilter]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  }, [location, defaultFilter]);

  const getPropertyPrice = (property: IProperty): string => {
    const formatBRL = (value: number) =>
      value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    if (property.is_venda && property.valor_substituto) {
      return `a partir de\n${formatBRL(property.valor_substituto)}`;
    }

    let valorExibido = 0;
    if (selectedOperation === 'ALUGUEL') {
      valorExibido = property.valor_aluguel || 0;
    } else {
      valorExibido = property.valor_venda || property.valor_aluguel || 0;
    }

    return formatBRL(valorExibido);
  };

  const getPropertyCod = (property: IProperty): string => {
    if (!property.cod_imovel) return `${property.id}`;
    if (typeof property.cod_imovel === 'string') {
      return `${property.cod_imovel}${property.id}`;
    }
    return `${property.cod_imovel.cod}${property.id}`;
  };

  return (
    <Styles.Body>
      <SEO
        title={
          defaultFilter === 'VENDA'
            ? 'Imóveis à Venda'
            : defaultFilter === 'ALUGUEL'
              ? 'Imóveis para Alugar'
              : 'Comprar, Vender e Alugar Imóveis'
        }
        description="Encontre o imóvel ideal para comprar, vender ou alugar. Casas, apartamentos, terrenos e mais com as melhores condições. Elite Prime Imobiliária em Sorocaba e região."
      />
      <Header />
      <Navbar />

      <Styles.FirstSection gridDirection={filterDirection}>
        {showCarousel && <Carousel />}
        <Filter gridDirection={filterDirection} />
      </Styles.FirstSection>

      {showOperationFilter && (
        <Styles.SecondSection>
          <OperationFilter setSelectedOperation={handleOperationChange} />
        </Styles.SecondSection>
      )}

      <Styles.ThirdSection>
        <Styles.PropertyGrid ref={propertyListRef}>
          {!!properties?.length &&
            properties.map((property, index) => (
              <PropertyCard
                id={property.id}
                key={`${property.id}${index}`}
                imageUrl={property.foto_capa}
                title={property.titulo || 'Sem título'}
                cod={getPropertyCod(property)}
                size={`${property.area_construida}`}
                rooms={`${property.quartos || 0}`}
                shower={`${property.banheiros || 0}`}
                garage={`${property.vagas || 0}`}
                propertyType={
                  property.tipo_imovel?.titulo ||
                  String(property.tipo_imovel || '')
                }
                price={getPropertyPrice(property)}
                operationType={
                  selectedOperation == 'TODOS'
                    ? property.is_venda
                      ? 'VENDA'
                      : 'ALUGUEL'
                    : selectedOperation
                }
              />
            ))}
        </Styles.PropertyGrid>
      </Styles.ThirdSection>

      <PageControl
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={handlePageChange}
        syncPageWithJQuery
      />

      <Styles.FourthSection>
        <AppBanner />
      </Styles.FourthSection>

      <Footer />
    </Styles.Body>
  );
};

export default HomeScreen;
